import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFoodSafetyContext, FS_SCREENS } from '../context/FoodSafetyContext';
import { foodSafetyService } from '../services/foodSafetyService';
import moment from 'moment';

const ppeItems = [
    "Hair Net Wearing Issue / Beard Net",
    "Apron (Torn, Dirty)",
    "Personal Hygiene (Nail without trimming/paint, Heena application)",
    "Jewellery Policy (Bracelet, Thread, Ring, Bindi)"
];

const newBlockLocations = [
    "Hand Wash Area", "Plant Entry", "Near Washing Area", "Near Emergency Gate", 
    "Near Packing Area", "Invert Syrup Room", "Broken Room", "Premixing Room", 
    "Mixing Entrance", "Washing Area", "Infront of Terrace Gate", 
    "New Plant Worker Entry 1", "New Plant Worker Entry 2", "Cream Room", 
    "Line 7 Packing", "RM Unloading Bay 1", "RM Unloading Bay 2", 
    "Line 6 Packing", "RM Store 1st Floor"
];

const oldBlockLocations = [
    "RM Unloading Bay", "Biscuit Grinding Room", "RM Storage", "Outside Cold Room", 
    "Maintenance Change Room", "Outside Maintenance Change Room", "Oven Area", 
    "Outside QA Lab", "Staff Hand Wash", "Outside Staff Canteen", "Staff Canteen", 
    "Near Worker Entry", "Worker Entry Hand Wash Area", "Outside HR Office", 
    "Near PM Warehouse Door", "Near PM Warehouse", "Near FG Warehouse Door", 
    "Outside DH Room", "Near Emergency Exit 1", "Near Emergency Exit 2", 
    "Near Emergency Exit 3", "Near Emergency Exit 4", "Near Emergency Exit 5", 
    "Wash Area", "Mixing Section HAAS Line", "Cooling Conveyor HAAS Line", 
    "Packing Section HAAS Line", "Outside Flavour Room", "New Cold Room Gallery 1", 
    "New Cold Room Gallery 2", "PM Warehouse", "FG Warehouse", "Worker Canteen", 
    "Mondelez Packing", "Line 8 Packing", "Line 8 Mixing", "FG Main Warehouse"
];

const gmpSections: Record<string, { id: number; text: string }[]> = {
    "Personal Hygiene": [
        { id: 1, text: "Protective clothing/head covering worn properly" },
        { id: 2, text: "Freedom from skin disease/wounds" },
        { id: 3, text: "No small articles carried in shirt pockets" },
        { id: 4, text: "Hand wash & IPA sanitization followed" },
        { id: 5, text: "No jewellery/glass bangles/flowers/bindi on shop floor" }
    ],
    "Mixing": [
        { id: 6, text: "Material identification maintained" },
        { id: 7, text: "Sieving before use done properly" },
        { id: 8, text: "Covered pre-weighed ingredients stored safely" },
        { id: 9, text: "Separate stitching-thread disposal maintained" },
        { id: 10, text: "Sieve & magnet condition checked & OK" },
        { id: 11, text: "Sieve collection disposal handled properly" },
        { id: 12, text: "Old dough storage & traceability maintained" },
        { id: 13, text: "Covered/cleaned storage containers used" },
        { id: 14, text: "Ingredient traceability record maintained" },
        { id: 15, text: "Strip curtain cleaning followed" },
        { id: 16, text: "Clean handling containers used" },
        { id: 17, text: "Mixer cleaned top & sides" },
        { id: 18, text: "Premixing/mixing floor cleaned" },
        { id: 19, text: "Broken-grinding section cleaned & trays identified" },
        { id: 20, text: "Defectives kept 18\" from wall" },
        { id: 21, text: "Defectives identified by variety" },
        { id: 22, text: "Biscuits sorted before grinding (overbaked/foreign matter removed)" },
        { id: 23, text: "Dust collection tray condition checked & OK" },
        { id: 24, text: "Metal detector effectiveness check done" },
        { id: 25, text: "Defective biscuits (usable/unusable) segregated at oven end" },
        { id: 26, text: "Windows/ventilators meshed & cleaned" },
        { id: 27, text: "Food-handling containers undamaged" },
        { id: 28, text: "Band cleaning/condition checked & OK" },
        { id: 29, text: "Dough collected & transferred safely" },
        { id: 30, text: "No water stagnation in mixing area" }
    ],
    "Packing": [
        { id: 31, text: "CBB/laminate storage on pallets away from wall" },
        { id: 32, text: "Cooling/packing conveyors in good condition" },
        { id: 33, text: "Rollers/scrapers/catch trays clean" },
        { id: 34, text: "No rejected rolls kept in packing" },
        { id: 35, text: "Catch trays under machines clean" },
        { id: 36, text: "Biscuit trays moved on trolleys (not dragged)" },
        { id: 37, text: "Defective biscuits passed through magnet before grinding" },
        { id: 38, text: "Tray-packed biscuits not touching tray above" },
        { id: 39, text: "Cleaning schedules followed" },
        { id: 40, text: "Day/shift/production-center code legible on pack" },
        { id: 41, text: "Empty CBB inspected before filling" },
        { id: 42, text: "No staple pins/rubber bands/threads allowed" },
        { id: 43, text: "Metal detector checked per line" },
        { id: 44, text: "Window condition OK" },
        { id: 45, text: "Floor clean" },
        { id: 46, text: "Biscuit trays periodically cleaned" }
    ]
};

const SummaryReporting: React.FC = () => {
    const { state, dispatch } = useFoodSafetyContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tourDetails, setTourDetails] = useState<any>(null);
    const [childItems, setChildItems] = useState<any[]>([]);

    useEffect(() => {
        loadSummaryData();
    }, [state.currentTourId]);

    const loadSummaryData = async () => {
        setLoading(true);
        try {
            const tourId = state.currentTourId;
            let parentRecord: any = null;

            if (tourId) {
                const tours = await foodSafetyService.getTourHistory();
                const cleanId = String(tourId).replace(/[{}]/g, "").trim().toLowerCase();
                parentRecord = tours.find((t: any) =>
                    String(t.cr3ea_prod_rajpura_quality_tourid || t.cr3ea_prod_qualitytourid || "")
                        .replace(/[{}]/g, "").trim().toLowerCase() === cleanId
                );

                const items = await foodSafetyService.getChecklistItems(tourId);
                setChildItems(items || []);
            }

            if (!parentRecord && state.sessionData) {
                parentRecord = {
                    cr3ea_food_safety_checklisttype: state.selectedChecklistType,
                    cr3ea_plantid: state.sessionData.site,
                    cr3ea_lineno: state.sessionData.line,
                    cr3ea_assigned_qa: state.sessionData.qaExecutive,
                    cr3ea_shiftexecutiveproduction: state.sessionData.productionIncharge,
                    cr3ea_food_safety_areaincharge: state.sessionData.areaIncharge,
                    cr3ea_food_safety_area: state.sessionData.area,
                    cr3ea_shift: state.selectedShift,
                    cr3ea_food_safety_cycle: state.sessionData.cycle,
                    cr3ea_tourstartdate: state.sessionData.tourStartDate,
                    cr3ea_tourcompletiondate: new Date().toISOString(),
                    cr3ea_overall_score: "100%",
                    cr3ea_checklist_result: "Pass"
                };
            }

            setTourDetails(parentRecord);
        } catch (error) {
            console.error("Failed to load tour summary:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        dispatch({ type: 'RESET_STATE' });
        navigate('/');
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading summary report...</p>
            </div>
        );
    }

    if (!tourDetails) {
        return (
            <div className="text-center py-5">
                <h5 className="text-danger">Summary data unavailable</h5>
                <button className="btn btn-primary mt-3" onClick={() => dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.CHECKLIST_INFO })}>
                    Back to Checklist Form
                </button>
            </div>
        );
    }

    const checklistType = tourDetails.cr3ea_food_safety_checklisttype || state.selectedChecklistType || "PPE Checklist";
    const siteVal = tourDetails.cr3ea_plantid || state.sessionData?.site || "Rajpura";
    const lineVal = tourDetails.cr3ea_lineno || state.sessionData?.line || "Line 1";
    const qaExec = tourDetails.cr3ea_assigned_qa || state.sessionData?.qaExecutive || "--";
    const prodIncharge = tourDetails.cr3ea_shiftexecutiveproduction || state.sessionData?.productionIncharge || "--";
    const areaIncharge = tourDetails.cr3ea_food_safety_areaincharge || state.sessionData?.areaIncharge;
    const pciArea = tourDetails.cr3ea_food_safety_area || state.sessionData?.area;
    const shiftVal = tourDetails.cr3ea_shift || state.selectedShift || "Shift 1";
    const cycleVal = tourDetails.cr3ea_food_safety_cycle || state.sessionData?.cycle || "Cycle-1";

    const startDate = tourDetails.cr3ea_tourstartdate;
    const completionDate = tourDetails.cr3ea_tourcompletiondate || tourDetails.modifiedon;
    let conductedDateText = "--";
    if (startDate) {
        conductedDateText = moment(startDate).format("DD-MM-YYYY hh:mm A");
        if (completionDate) {
            conductedDateText += ` to ${moment(completionDate).format("DD-MM-YYYY hh:mm A")}`;
        }
    }

    const overallScore = tourDetails.cr3ea_overall_score || "100%";
    const auditResult = tourDetails.cr3ea_checklist_result || "Pass";
    const isPass = auditResult === "Pass";

    return (
        <div>
            {/* Stepper Workflow Bar */}
            <div className="no-print" style={{ marginBottom: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 16px', borderRadius: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#e2e8f0', color: '#475569', padding: '4px 10px', borderRadius: '4px', fontWeight: 600, fontSize: '13px' }}>
                        1. Request Info
                    </span>
                    <span style={{ color: '#94a3b8' }}>➔</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#e2e8f0', color: '#475569', padding: '4px 10px', borderRadius: '4px', fontWeight: 600, fontSize: '13px' }}>
                        2. {checklistType}
                    </span>
                    <span style={{ color: '#94a3b8' }}>➔</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#80cbcf', color: '#ffffff', padding: '4px 12px', borderRadius: '4px', fontWeight: 600, fontSize: '13px' }}>
                        3. Summary Report
                    </span>
                </div>
            </div>

            {/* Main Summary Card matching reference source code */}
            <div style={{ padding: '24px', border: '1px solid #10b981', borderRadius: '8px', backgroundColor: '#ffffff', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                
                {/* Header Action Bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #f1f5f9', paddingBottom: '16px', marginBottom: '20px' }}>
                    <h4 style={{ fontWeight: 700, color: '#0f766e', margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px' }}>
                        <i className="bi bi-file-earmark-check-fill"></i> Checklist Summary Report
                    </h4>
                    <div className="no-print" style={{ display: 'flex', gap: '10px' }}>
                        <button
                            type="button"
                            onClick={handlePrint}
                            style={{
                                backgroundColor: '#f1f5f9',
                                color: '#1e293b',
                                border: '1px solid #cbd5e1',
                                height: '40px',
                                padding: '0 18px',
                                borderRadius: '6px',
                                fontWeight: 600,
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <i className="bi bi-printer"></i> Print Report
                        </button>
                        <button
                            type="button"
                            onClick={handleClose}
                            style={{
                                backgroundColor: '#80cbcf',
                                color: '#ffffff',
                                border: 'none',
                                height: '40px',
                                padding: '0 20px',
                                borderRadius: '6px',
                                fontWeight: 600,
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <i className="bi bi-house-door"></i> Close & Go Home
                        </button>
                    </div>
                </div>

                {/* Metadata Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 16px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Checklist Type</span>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginTop: '4px' }}>{checklistType}</div>
                    </div>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 16px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Manufacturing Site / Line</span>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginTop: '4px' }}>{siteVal} / {lineVal}</div>
                    </div>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 16px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>QA Executive</span>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginTop: '4px' }}>{qaExec}</div>
                    </div>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 16px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Production Incharge</span>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginTop: '4px' }}>{prodIncharge}</div>
                    </div>
                    {areaIncharge && (
                        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 16px', borderRadius: '6px' }}>
                            <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Area Incharge</span>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginTop: '4px' }}>{areaIncharge}</div>
                        </div>
                    )}
                    {checklistType === "PCI Checklist" && pciArea && (
                        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 16px', borderRadius: '6px' }}>
                            <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Inspection Area / Block</span>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginTop: '4px' }}>{pciArea}</div>
                        </div>
                    )}
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 16px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Shift / Cycle</span>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginTop: '4px' }}>{shiftVal} / {cycleVal}</div>
                    </div>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 16px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Conducted Date & Time</span>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginTop: '4px' }}>{conductedDateText}</div>
                    </div>
                </div>

                {/* Score and Result Status Row */}
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '25px' }}>
                    <div
                        style={{
                            flex: 1,
                            minWidth: '260px',
                            border: `1px solid ${isPass ? '#bbf7d0' : '#fecaca'}`,
                            backgroundColor: isPass ? '#f0fdf4' : '#fee2e2',
                            padding: '18px 24px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '20px'
                        }}
                    >
                        <div
                            style={{
                                width: '74px',
                                height: '74px',
                                flexShrink: 0,
                                borderRadius: '50%',
                                backgroundColor: isPass ? '#10b981' : '#ef4444',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                fontWeight: 800,
                                boxShadow: `0 4px 6px -1px ${isPass ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                            }}
                        >
                            {overallScore}
                        </div>
                        <div>
                            <h5 style={{ margin: 0, fontWeight: 700, color: isPass ? '#065f46' : '#991b1b', fontSize: '16px' }}>
                                Compliance Score
                            </h5>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: isPass ? '#047857' : '#b91c1c', fontWeight: 600 }}>
                                Audit Result: {auditResult}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Detailed Checkpoints List */}
                <div>
                    <h5 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '14px', fontSize: '15px' }}>
                        Checkpoint Observations Log
                    </h5>

                    <div style={{ overflowX: 'auto', border: '1px solid #cbd5e1', borderRadius: '6px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8fafc', fontWeight: 700, borderBottom: '1px solid #cbd5e1' }}>
                                    <th style={{ padding: '12px 14px', width: '8%', color: '#475569' }}>Sr No.</th>
                                    <th style={{ padding: '12px 14px', textAlign: 'left', width: '52%', color: '#475569' }}>Checkpoint Criteria</th>
                                    <th style={{ padding: '12px 14px', width: '20%', color: '#475569' }}>Status / Value</th>
                                    <th style={{ padding: '12px 14px', textAlign: 'left', width: '20%', color: '#475569' }}>Observations / Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {checklistType === "PPE Checklist" && ppeItems.map((itemText, idx) => {
                                    const match = childItems.find(c => c.cr953_food_safety_criteria === itemText);
                                    const defectCount = match ? (parseInt(match.cr953_food_safety_defectcount) || 0) : 0;
                                    const remarks = match ? (match.cr953_food_safety_defectremarks || "--") : "--";

                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #cbd5e1', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fcfdfe' }}>
                                            <td style={{ padding: '10px 14px', fontWeight: 700, color: '#475569' }}>{idx + 1}</td>
                                            <td style={{ padding: '10px 14px', textAlign: 'left', color: '#1e293b', fontWeight: 500 }}>{itemText}</td>
                                            <td style={{ padding: '10px 14px' }}>
                                                <span
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '11px',
                                                        backgroundColor: defectCount > 0 ? '#fee2e2' : '#dcfce7',
                                                        color: defectCount > 0 ? '#b91c1c' : '#15803d',
                                                        border: `1px solid ${defectCount > 0 ? '#fecaca' : '#bbf7d0'}`,
                                                        borderRadius: '9999px',
                                                        fontWeight: 700,
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    {defectCount} Defects
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px 14px', textAlign: 'left', color: '#475569' }}>{remarks}</td>
                                        </tr>
                                    );
                                })}

                                {checklistType === "GMP Checklist" && Object.keys(gmpSections).map(secName =>
                                    gmpSections[secName].map(item => {
                                        const match = childItems.find(c => c.cr953_food_safety_criteria === item.text);
                                        const status = match ? (match.cr953_food_safety_status || "Okay") : "Okay";
                                        const remarks = match ? (match.cr953_food_safety_defectremarks || "--") : "--";

                                        return (
                                            <tr key={item.id} style={{ borderBottom: '1px solid #cbd5e1', backgroundColor: item.id % 2 === 0 ? '#ffffff' : '#fcfdfe' }}>
                                                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#475569' }}>{item.id}</td>
                                                <td style={{ padding: '10px 14px', textAlign: 'left' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#0284c7', textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>
                                                        {secName}
                                                    </span>
                                                    <span style={{ color: '#1e293b', fontWeight: 500 }}>{item.text}</span>
                                                </td>
                                                <td style={{ padding: '10px 14px' }}>
                                                    <span
                                                        style={{
                                                            padding: '4px 10px',
                                                            fontSize: '11px',
                                                            backgroundColor: status === "Okay" ? '#dcfce7' : status === "Not Okay" ? '#fee2e2' : '#f1f5f9',
                                                            color: status === "Okay" ? '#15803d' : status === "Not Okay" ? '#b91c1c' : '#475569',
                                                            border: `1px solid ${status === "Okay" ? '#bbf7d0' : status === "Not Okay" ? '#fecaca' : '#cbd5e1'}`,
                                                            borderRadius: '9999px',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >
                                                        {status}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '10px 14px', textAlign: 'left', color: '#475569' }}>{remarks}</td>
                                            </tr>
                                        );
                                    })
                                )}

                                {checklistType === "PCI Checklist" && (() => {
                                    const selectedLocations = (pciArea === "New Block") ? newBlockLocations : oldBlockLocations;

                                    return selectedLocations.map((locName, idx) => {
                                        const matches = childItems.filter(c => c.cr953_food_safety_location === locName);
                                        let isOkay = true;
                                        let obsText = "--";

                                        if (matches.length > 0) {
                                            isOkay = matches.every(m => m.cr953_food_safety_status === "Okay");
                                            if (!isOkay) {
                                                const obsItems = matches
                                                    .filter(m => m.cr953_food_safety_status === "Not Okay")
                                                    .map(m => `${m.cr953_food_safety_observationtype || m.cr953_food_safety_criteria || 'Observation'} (${m.cr953_food_safety_defectcount || 1})`);
                                                if (obsItems.length > 0) obsText = obsItems.join(", ");
                                            }
                                        }

                                        return (
                                            <tr key={idx} style={{ borderBottom: '1px solid #cbd5e1', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fcfdfe' }}>
                                                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#475569' }}>{idx + 1}</td>
                                                <td style={{ padding: '10px 14px', textAlign: 'left', color: '#1e293b', fontWeight: 500 }}>{locName}</td>
                                                <td style={{ padding: '10px 14px' }}>
                                                    <span
                                                        style={{
                                                            padding: '4px 10px',
                                                            fontSize: '11px',
                                                            backgroundColor: isOkay ? '#dcfce7' : '#fee2e2',
                                                            color: isOkay ? '#15803d' : '#b91c1c',
                                                            border: `1px solid ${isOkay ? '#bbf7d0' : '#fecaca'}`,
                                                            borderRadius: '9999px',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >
                                                        {isOkay ? "Okay" : "Not Okay"}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '10px 14px', textAlign: 'left', color: '#475569' }}>{obsText}</td>
                                            </tr>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SummaryReporting;
